import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useState } from "react"
import { fontSizeFactorStep } from "src/utilities/constants"

import { Footer } from "../../components/common/Footer"
import { useModalDismiss } from "../../hooks/use-modal-dismiss"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate from "../../hooks/use-translate"
import { appStore } from "../../store/AppStore"
import { comb, s } from "../../utilities/common"
import { theme } from "../../utilities/theme"
import { usePageviewTracking } from "../Detail/use-pageview-tracking"

import { SliderBar } from "./components/SliderBar"
import { SwitchBar } from "./components/SwitchBar"
import { SettingsFocusKeys } from "./settings-focus-keys"

export const AccessibilityModal = ({
  open,
  returnFocus,
}: {
  open: comb<boolean>
  returnFocus: string
}) => {
  if (!open.val) return null
  return <Screen open={open} returnFocus={returnFocus} />
}

const Screen = ({
  open,
  returnFocus,
  onClose,
}: {
  open: comb<boolean>
  returnFocus: string
  onClose?: () => void
}) => {
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })
  const accessibility = appStore((state) => state.accessibility)
  const setAccessibility = appStore((state) => state.setAccessibility)

  const track = usePageviewTracking()
  useEffect(() => {
    track("Settings", "settings/accessibility")
  }, [])

  useModalDismiss(() => {
    setFocus(returnFocus)
    if (fontSizeFactorVal !== fontSizeFactorStep) {
      window.localStorage.setItem(
        "dg_accessibility_fontsize",
        fontSizeFactorVal.toString(),
      )
      document.getElementById("root")!.innerHTML = ""
      document.getElementById("root")!.style.transform = "translate3d(0, 0, 0)"
      setTimeout(() => {
        window.location.reload()
      }, 200)
    }
    open.set(false)
    onClose?.()
  })

  useEffect(() => {
    setFocus(SettingsFocusKeys.AccessibilityDefault)
  }, [setFocus])
  const [switchTransitions, setSwitchTransitions] = useState(
    accessibility.transitions,
  )
  const handleSwitchTransitions = (e: boolean) => {
    setSwitchTransitions(e)
    setAccessibility({ type: "transitions", value: e })
  }

  const [fontSizeFactorVal, setFontSizeFactorVal] =
    useState<number>(fontSizeFactorStep)

  const transition = useTransitions()
  const t = useTranslate()

  return (
    <Modal
      open={open.val}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.variant,
        "& > *": { transition: "none !important" },
      }}
    >
      <DialogContent>
        <Box
          style={{ transition }}
          sx={{
            height: "auto",
            width: "100%",
            backgroundColor: theme.palette.primary.variant,
            position: "absolute",
            overflowY: "visible",
            minHeight: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <FocusContext.Provider value={focusKey}>
              <Box
                ref={ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: s("1160px"),
                  mt: s("184px"),
                  "& > :not(:last-child)": {
                    mb: s("60px"),
                  },
                }}
              >
                <SwitchBar
                  focusKey={SettingsFocusKeys.AccessibilityDefault}
                  onChange={handleSwitchTransitions}
                  title={t("settings_transition_animation")}
                  checked={switchTransitions}
                ></SwitchBar>
                <SliderBar
                  onChange={(_, v) => setFontSizeFactorVal(v as number)}
                  value={comb.ine(fontSizeFactorVal, setFontSizeFactorVal)}
                ></SliderBar>
              </Box>
            </FocusContext.Provider>
          </Box>
          <Footer />
        </Box>
      </DialogContent>
    </Modal>
  )
}
